import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import api from '../../utils/api';
import { formatDateTime } from '../../utils/helpers';
import './Header.css';

export default function Header({ title, subtitle, actions }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
      setUnread(res.data.filter(n => !n.isRead).length);
    } catch {}
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markAllRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({...n, isRead: true})));
      setUnread(0);
    } catch {}
  };

  const markRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n._id === id ? {...n, isRead: true} : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const getArtworkId = (notification) => {
    if (!notification?.artworkId) return null;
    if (typeof notification.artworkId === 'string') return notification.artworkId;
    return notification.artworkId._id || notification.artworkId.id || null;
  };

  const handleNotificationClick = async (notification) => {
    const artworkId = getArtworkId(notification);
    await markRead(notification._id);
    setOpen(false);
    if (artworkId) {
      navigate(`/artworks/${artworkId}`);
    }
  };

  const typeIcon = (type) => {
    const icons = {
      approval_required: '🎨',
      stage_complete: '✅',
      changes_requested: '⚠️',
      artwork_ready: '📦',
      position_proof: '📋',
      colour_proof: '🎨'
    };
    return icons[type] || '🔔';
  };

  return (
    <header className="page-header">
      <div>
        <h1 className="header-title">{title}</h1>
        {subtitle && <p className="header-subtitle">{subtitle}</p>}
      </div>

      <div className="header-actions" ref={ref}>
        {actions && <div className="header-custom-actions">{actions}</div>}
        <button className="notif-btn" onClick={() => setOpen(!open)}>
          <Bell size={20} />
          {unread > 0 && <span className="notif-badge">{unread > 9 ? '9+' : unread}</span>}
        </button>

        {open && (
          <div className="notif-dropdown animate-fadeInUp">
            <div className="notif-header">
              <span className="notif-header-title">Notifications</span>
              {unread > 0 && (
                <button className="notif-mark-all" onClick={markAllRead}>
                  <CheckCheck size={14} /> Mark all read
                </button>
              )}
            </div>

            <div className="notif-list">
              {notifications.length === 0 ? (
                <div className="notif-empty">
                  <Bell size={24} />
                  <p>No notifications yet</p>
                </div>
              ) : (
                notifications.slice(0, 15).map(n => (
                  <div
                    key={n._id}
                    className={`notif-item ${!n.isRead ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(n)}
                  >
                    <span className="notif-icon">{typeIcon(n.type)}</span>
                    <div className="notif-content">
                      <p className="notif-title">{n.title}</p>
                      <p className="notif-msg">{n.message}</p>
                      <p className="notif-time">{formatDateTime(n.createdAt)}</p>
                    </div>
                    {!n.isRead && <span className="notif-dot" />}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
